import React from 'react';
import { TutorialPage } from '../TutorialPage';
import { BarChart3, DollarSign, Lightbulb } from 'lucide-react';

export interface CluesPageProps {
  currentPage: number;
  totalPages: number;
  onNext: () => void;
  onPrevious: () => void;
  onSkip: () => void;
  onFinish: () => void;
}

/**
 * Tutorial Page 3: Explore the Clues
 * Uses viewport-top positioning to sit above multiple spotlighted elements
 */
export const CluesPage: React.FC<CluesPageProps> = (props) => {
  return (
    <TutorialPage
      title=""
      viewportPosition="top"
      cardClassName="max-w-5xl"
      {...props}
    >
      {/* Custom layout with title on left and cards on right */}
      <div className="flex flex-row items-center gap-6 -my-2">
        {/* Left: Title and subtitle */}
        <div className="flex flex-col gap-1 flex-shrink-0" style={{ minWidth: '180px' }}>
          <h3 className="text-xl font-bold text-foreground">
            Explore the Clues
          </h3>
          <p className="text-xs text-muted-foreground italic">
            A new hint will be revealed after each guess.
          </p>
        </div>

        {/* Right: Three-column card grid */}
        <div className="grid grid-cols-3 gap-3 flex-1">
          {/* Price Chart Card */}
          <div className="flex flex-row items-start gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="font-semibold text-sm text-foreground text-left">Price Chart</h4>
              <p className="text-xs text-muted-foreground text-left">
                Analyze historical price movements
              </p>
            </div>
          </div>

          {/* Financial Metrics Card */}
          <div className="flex flex-row items-start gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="font-semibold text-sm text-foreground text-left">Financial Metrics</h4>
              <p className="text-xs text-muted-foreground text-left">
                Review key financial data
              </p>
            </div>
          </div>

          {/* Hints Card */}
          <div className="flex flex-row items-start gap-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lightbulb className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="font-semibold text-sm text-foreground text-left">Hints</h4>
              <p className="text-xs text-muted-foreground text-left">
                Unlock hints with each guess
              </p>
            </div>
          </div>
        </div>
      </div>
    </TutorialPage>
  );
};

export default CluesPage;
